import { ExternalLink, Mail, Phone, Award, Building2 } from 'lucide-react';
import { Reveal, Eyebrow } from './Reveal';
import { ASSETS, CONTACT } from '../constants/site';

const COMPANIES = ['LG CNS', 'CK&B', 'Compax Digital', 'BFLabs', 'SIOT InfoTech', 'MICUBE Solution'];
const CERTS = ['Microsoft Specialist: C#', 'MCSE: Private Cloud', 'Cisco CCNP', 'CompTIA Security+'];

export default function Credentials() {
  return (
    <section id="engineer" data-testid="credentials-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-slate-700/40 min-h-[420px] group">
            <img src={ASSETS.workstation} alt="Engineering workstation" className="absolute inset-0 h-full w-full object-cover opacity-50 transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/60 to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-8">
              <img src={ASSETS.emblem} alt="" className="h-16 w-16 rounded-xl mb-4 border border-amber-500/30" />
              <div className="font-display font-bold text-2xl">Gabe Kim</div>
              <div className="font-mono-tech text-xs tracking-[0.2em] text-amber-300/90 uppercase mt-1">
                Founder & Lead Engineer — GSLAB
              </div>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <Eyebrow>Why GSLAB</Eyebrow>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              Enterprise rigor, <span className="text-gradient-gold">small-business simple.</span>
            </h2>
            <p className="mt-5 text-slate-400 text-base leading-relaxed">
              Your site is engineered by a Georgia Tech computer engineer — M.S. & B.S. in Electrical &
              Computer Engineering — with 8+ years building enterprise systems across telecom, healthcare,
              banking, IoT and AI, including national-scale platforms at LG CNS.
            </p>
          </Reveal>

          <Reveal i={1}>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[['8+', 'YEARS EXPERIENCE'], ['15+', 'SYSTEMS SHIPPED'], ['6', 'INDUSTRIES']].map(([v, l]) => (
                <div key={l} className="border border-slate-800 rounded-xl bg-[#0F1C33]/60 px-4 py-4">
                  <div className="font-display font-extrabold text-2xl text-gradient-gold">{v}</div>
                  <div className="font-mono-tech text-[9px] tracking-[0.2em] text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal i={2}>
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={15} className="text-slate-500" />
                <span className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500 uppercase">Enterprise Track Record</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {COMPANIES.map((c) => (
                  <span key={c} className="text-xs border border-slate-700 rounded-full px-4 py-1.5 text-slate-300">{c}</span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal i={3}>
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3">
                <Award size={15} className="text-slate-500" />
                <span className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500 uppercase">Certifications</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {CERTS.map((c) => (
                  <span key={c} className="text-xs border border-amber-500/25 bg-amber-500/5 rounded-full px-4 py-1.5 text-amber-200/90">{c}</span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal i={4}>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                data-testid="credentials-portfolio-link"
                href={CONTACT.portfolio}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-amber-500/40 hover:border-amber-400 text-amber-300 rounded-full px-6 py-3 text-sm font-semibold transition-colors duration-300"
              >
                Full Portfolio & Resume <ExternalLink size={15} />
              </a>
              <a data-testid="credentials-email-link" href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-amber-300 transition-colors py-3">
                <Mail size={15} /> {CONTACT.email}
              </a>
              <a data-testid="credentials-phone-link" href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-amber-300 transition-colors py-3">
                <Phone size={15} /> {CONTACT.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
